import React, { useEffect, useState } from 'react';
import { Circle } from 'lucide-react';

/**
 * RestaurantStatusControl — restaurant-wide manual status toggle with
 * optional auto-resume timer.
 *
 * Lets the owner pause new orders without changing business hours. The
 * actual gate (block checkout, surface the banner, refuse the POST /orders)
 * lives on the public menu + backend; this component is purely the UI.
 *
 * Status semantics:
 *   - "open" (default): operate normally. Business hours still apply.
 *   - "busy": override — refuses new orders even if inside business hours.
 *             Menu / photos / prices / reviews remain fully visible.
 *
 * Auto-resume:
 *   When the owner picks "Busy for 30 min / 1 hour / 2 hours", we compute
 *   an ISO datetime in the future and pass it as the second arg to
 *   `onChange(status, untilISO)`. The caller forwards `manual_status_until`
 *   to the backend. The server refuses orders only while `now < until`,
 *   then automatically flips the restaurant back to Open.
 *
 * Props:
 *   - value:    current status ('open' | 'busy'). Defaults to 'open'.
 *   - until:    ISO datetime when busy should auto-resume (or null/empty).
 *   - onChange: (status, untilISO) — caller persists both fields.
 *   - light:    true to use the white/slate look (Dashboard).
 *   - testid:   optional data-testid prefix.
 */

const DURATION_PRESETS = [
  { value: 'manual', label: 'Until I switch back',  mins: 0   },
  { value: '30m',    label: '30 minutes',           mins: 30  },
  { value: '1h',     label: '1 hour',               mins: 60  },
  { value: '2h',     label: '2 hours',              mins: 120 },
];

// (countdown re-render is handled inline via `now` state below — no
//  separate ticker hook needed)

const fmtCountdown = (ms) => {
  if (ms <= 0) return 'resuming…';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const remM = m % 60;
    return `${h}h ${remM}m`;
  }
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

const RestaurantStatusControl = ({
  value,
  until,
  onChange,
  light = false,
  testid = 'restaurant-status',
}) => {
  const current = value === 'busy' ? 'busy' : 'open';
  // We need `Date.now()` for the countdown — but reading it directly in
  // the component body trips React's purity rule. Snapshot it into state
  // and let the ticker refresh it once per second so the countdown stays
  // accurate without polluting the render with impure reads.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (current !== 'busy' || !until) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [current, until]);

  const untilDate = until ? new Date(until) : null;
  const untilMs = untilDate ? untilDate.getTime() : null;
  const isCountingDown = current === 'busy' && untilMs != null && untilMs > now;

  // ── click handlers ─────────────────────────────────────────────────
  const switchToOpen = () => onChange?.('open', null);

  const switchToBusy = (preset) => {
    const def = DURATION_PRESETS.find((p) => p.value === preset) || DURATION_PRESETS[0];
    if (!def.mins) {
      onChange?.('busy', null);
      return;
    }
    const iso = new Date(now + def.mins * 60 * 1000).toISOString();
    onChange?.('busy', iso);
  };

  // ── style helpers ──────────────────────────────────────────────────
  const baseBtn =
    'flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-[13.5px] font-bold transition-all';

  const openClass = light
    ? (current === 'open'
        ? 'bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm'
        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50')
    : (current === 'open'
        ? 'bg-emerald-500/15 border-emerald-500/45 text-emerald-200 shadow-[0_8px_24px_-12px_rgba(16,185,129,0.45)]'
        : 'bg-white/[0.04] border-white/12 text-white/55 hover:border-white/25');

  const busyClass = light
    ? (current === 'busy'
        ? 'bg-amber-50 border-amber-300 text-amber-700 shadow-sm'
        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50')
    : (current === 'busy'
        ? 'bg-amber-500/15 border-amber-500/45 text-amber-200 shadow-[0_8px_24px_-12px_rgba(245,158,11,0.45)]'
        : 'bg-white/[0.04] border-white/12 text-white/55 hover:border-white/25');

  // Active preset for the radio-group highlight — derived from `until`.
  const activePreset = (() => {
    if (current !== 'busy' || !untilDate) return 'manual';
    const remainingMin = Math.round((untilMs - now) / 60000);
    if (remainingMin <= 30) return '30m';
    if (remainingMin <= 60) return '1h';
    return '2h';
  })();

  return (
    <div data-testid={testid}>
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={switchToOpen}
          aria-pressed={current === 'open'}
          data-testid={`${testid}-open-btn`}
          className={`${baseBtn} ${openClass}`}
        >
          <Circle
            className={`w-3.5 h-3.5 ${current === 'open' ? 'fill-emerald-400 text-emerald-400' : 'text-slate-400'}`}
            strokeWidth={current === 'open' ? 0 : 2}
          />
          Open
        </button>
        <button
          type="button"
          onClick={() => switchToBusy('manual')}
          aria-pressed={current === 'busy'}
          data-testid={`${testid}-busy-btn`}
          className={`${baseBtn} ${busyClass}`}
        >
          <Circle
            className={`w-3.5 h-3.5 ${current === 'busy' ? 'fill-amber-400 text-amber-400' : 'text-slate-400'}`}
            strokeWidth={current === 'busy' ? 0 : 2}
          />
          Busy / Paused
        </button>
      </div>

      {/* Auto-resume duration picker — only when busy. Each preset persists
          a fresh `until` so picking "1 hour" after being paused 10 min
          extends the auto-resume to exactly 1 hour from now. */}
      {current === 'busy' && (
        <div
          data-testid={`${testid}-duration-picker`}
          className={`mt-3 rounded-xl border p-3 ${
            light ? 'border-amber-200 bg-amber-50/60' : 'border-amber-500/25 bg-amber-500/[0.04]'
          }`}
        >
          <p
            className={`text-[10.5px] uppercase tracking-[0.18em] font-bold mb-2 ${
              light ? 'text-amber-700' : 'text-amber-300/80'
            }`}
          >
            Auto-resume to Open
          </p>
          <div className="flex flex-wrap gap-1.5">
            {DURATION_PRESETS.map((p) => {
              const active = activePreset === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => switchToBusy(p.value)}
                  data-testid={`${testid}-duration-${p.value}`}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-bold transition-colors border ${
                    light
                      ? (active
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300')
                      : (active
                          ? 'bg-amber-500/25 border-amber-500/55 text-amber-100'
                          : 'bg-white/[0.04] border-white/10 text-white/65 hover:border-white/25')
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          {isCountingDown && (
            <p
              data-testid={`${testid}-countdown`}
              className={`mt-2.5 text-[12px] font-semibold ${
                light ? 'text-amber-800' : 'text-amber-200'
              }`}
            >
              Resuming Open in {fmtCountdown(untilMs - now)}
            </p>
          )}
        </div>
      )}

      {current === 'busy' ? (
        <p
          data-testid={`${testid}-helper-busy`}
          className={`mt-3 text-[12.5px] leading-relaxed ${light ? 'text-amber-700' : 'text-amber-200/85'}`}
        >
          Customers will see this restaurant as <span className="font-bold">Busy / Temporarily Paused</span>.
          The menu stays fully visible — photos, prices, reviews — but checkout is disabled until you switch back to Open.
        </p>
      ) : (
        <p
          data-testid={`${testid}-helper-open`}
          className={`mt-3 text-[12.5px] leading-relaxed ${light ? 'text-slate-500' : 'text-white/55'}`}
        >
          Operating normally. Business Hours still apply — switch to <span className="font-bold">Busy / Paused</span> any time the kitchen needs to catch up on existing orders.
        </p>
      )}
    </div>
  );
};

export default RestaurantStatusControl;
